import React, { useRef } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import QRCodeSVG from 'react-native-qrcode-svg'
import Clipboard from '@react-native-clipboard/clipboard'
import { Button, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { eventPublicUrl, shareFile, showErrorToast, showSuccessToast } from '@utils'
import { iconApp } from '@assets'
import type { KtEvent } from '@hooks'

const QR_CONTAINER_ID = 'event-share-qr'

/** The react-native-svg root handed over by `getRef` (native PNG export). */
interface QrSvgRef {
  toDataURL: (callback: (base64: string) => void) => void
}
const PNG_SIZE = 1024

/**
 * Converts the rendered QR SVG into a 1024 px PNG, ready to print on a poster
 * or to drop into Canva / Word / a slide.
 */
async function downloadQrAsPng(fileName: string, background: string): Promise<void> {
  const svg = document.querySelector<SVGSVGElement>(`#${QR_CONTAINER_ID} svg`)
  if (!svg) throw new Error('QR code not rendered')
  const markup = new XMLSerializer().serializeToString(svg)
  const svgUrl = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }))
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new window.Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = svgUrl
    })
    const canvas = document.createElement('canvas')
    canvas.width = PNG_SIZE
    canvas.height = PNG_SIZE
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Canvas unavailable')
    context.fillStyle = background
    context.fillRect(0, 0, PNG_SIZE, PNG_SIZE)
    context.drawImage(image, 0, 0, PNG_SIZE, PNG_SIZE)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!blob) throw new Error('PNG export failed')
    const pngUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = pngUrl
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(pngUrl)
  } finally {
    URL.revokeObjectURL(svgUrl)
  }
}

/** Link + QR code of the event: the two ways people register without an invitation. */
export function SharePanel({ event }: { event: KtEvent }) {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const url = eventPublicUrl(event.code)

  const copy = async () => {
    try {
      if (Platform.OS === 'web' && navigator.clipboard) await navigator.clipboard.writeText(url)
      else Clipboard.setString(url)
      showSuccessToast(t('events.manage.share.copied'), url)
    } catch {
      showErrorToast(t('errors.title'), t('errors.generic'))
    }
  }

  const download = async () => {
    try {
      await downloadQrAsPng(`qr-${event.code.toLowerCase()}.png`, theme.colors.white)
    } catch {
      showErrorToast(t('errors.title'), t('errors.generic'))
    }
  }

  // Native: the rendered QR (react-native-svg) exported as a PNG, then the share
  // sheet — "Save Image", AirDrop, Mail… stand in for the web download.
  const qrRef = useRef<QrSvgRef | null>(null)
  const shareQr = () => {
    const svg = qrRef.current
    if (!svg) {
      showErrorToast(t('errors.title'), t('errors.generic'))
      return
    }
    // No size option: on iOS it enlarges the canvas, not the drawing. The PNG comes
    // out at the rendered size × screen density (600 px on a 3x iPhone).
    svg.toDataURL((base64) => {
      shareFile({
        fileName: `qr-${event.code.toLowerCase()}.png`,
        base64,
        fallbackMessage: t('events.native.shareMessage', { name: event.name, url }),
        title: event.name,
      }).catch(() => showErrorToast(t('errors.title'), t('errors.generic')))
    })
  }

  return (
    <View style={styles.panel}>
      <Text type="subtitleBold">{t('events.manage.share.title')}</Text>
      <Text type="bodySmall" color={theme.colors.greyBlue}>
        {t('events.manage.share.hint')}
      </Text>
      <View nativeID={QR_CONTAINER_ID} style={styles.qr}>
        <QRCodeSVG
          getRef={(svg: QrSvgRef | null) => {
            qrRef.current = svg
          }}
          value={url}
          size={200}
          color={theme.colors.black}
          backgroundColor={theme.colors.white}
          logo={iconApp}
          logoSize={44}
          logoBackgroundColor={theme.colors.white}
          logoMargin={4}
          logoBorderRadius={10}
          quietZone={12}
          ecl="H"
        />
      </View>
      <View style={styles.linkBox}>
        <Text type="bodySmall" numberOfLines={1} selectable style={styles.link}>
          {url}
        </Text>
      </View>
      <View style={styles.actions}>
        <Button
          title={t('events.manage.share.copy')}
          iconName="link2"
          isSmall
          onPress={copy}
          containerStyle={styles.action}
        />
        {Platform.OS === 'web' ? (
          <Button
            title={t('events.manage.share.downloadQr')}
            iconName="download"
            isSmall
            unfilled
            onPress={download}
            containerStyle={styles.action}
          />
        ) : (
          <Button
            title={t('events.native.shareQr')}
            iconName="share"
            isSmall
            unfilled
            onPress={shareQr}
            containerStyle={styles.action}
          />
        )}
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    panel: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.xl,
      gap: theme.spacing.md,
    },
    qr: {
      alignSelf: 'center',
      padding: theme.spacing.sm,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
    },
    linkBox: {
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.sidebar,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
    },
    link: { color: theme.colors.primary },
    actions: { gap: theme.spacing.sm },
    action: { width: '100%' },
  })
